import { forwardRef } from 'react';
import styles from './Input.module.css';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  leadingIcon?: string;
  trailingIcon?: string;
}

/**
 * Suppress password-manager and browser autofill by default.
 *
 * Almost every field in this app describes *someone else* — the employee being
 * hired, the employer being recorded — yet react-hook-form's `register()` puts
 * `name="payment.name"`, `name="payment.email"`, `name="street"` on the DOM, and
 * nothing here set `autoComplete`. That combination is exactly what 1Password and
 * Chrome match on, so creating an employee prompted the operator to fill in their
 * own details, or to "save a new login".
 *
 * A field that names a real `autoComplete` token is asking for autofill, so it gets
 * none of this. Spreading before `{...props}` is not enough on its own, and that was
 * a real regression: `data-1p-ignore` makes 1Password skip a field *whatever* its
 * autocomplete token says, so LoginPage overrode `autoComplete` to
 * `email`/`current-password` and 1Password still refused to offer a login. Deriving
 * the suppression from the caller's own intent is what keeps the two in step —
 * LoginPage, RegisterPage, ResetPasswordPage, SecurityTab and PersonalTab need no
 * opt-out of their own.
 *
 * The legacy vanilla app solved this by propagating `data-1p-ignore` from the form
 * down to its inputs (`frontend/js/app.js`); the behaviour was lost in the React
 * port. `data-lpignore` is LastPass's equivalent.
 */
const AUTOFILL_OFF = {
  autoComplete: 'off',
  'data-1p-ignore': '',
  'data-lpignore': 'true',
} as const;

/** Whether the caller asked for autofill by naming a real autocomplete token. */
function wantsAutofill(autoComplete: string | undefined): boolean {
  return autoComplete != null && autoComplete !== '' && autoComplete !== 'off';
}

/** Clamp the year in a date string to at most 4 digits (e.g. "20265-06-01" → "2026-06-01"). */
function clampDateYear(value: string): string {
  const parts = value.split('-');
  if (parts[0] && parts[0].length > 4) {
    parts[0] = parts[0].slice(0, 4);
    return parts.join('-');
  }
  return value;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { error, leadingIcon, trailingIcon, className, type, onChange, ...props },
  ref
) {
  const classes = [
    styles.input,
    error && styles.error,
    leadingIcon && styles.hasLeading,
    trailingIcon && styles.hasTrailing,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Withheld entirely when the caller named an autocomplete token — see AUTOFILL_OFF.
  const autofillOff = wantsAutofill(props.autoComplete) ? undefined : AUTOFILL_OFF;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (type === 'date' && e.target.value) {
      const clamped = clampDateYear(e.target.value);
      if (clamped !== e.target.value) {
        e.target.value = clamped;
      }
    }
    onChange?.(e);
  }

  if (leadingIcon || trailingIcon) {
    return (
      <div className={styles.wrapper}>
        {leadingIcon && (
          <i className={`fa-solid ${leadingIcon} ${styles.leadingIcon}`} aria-hidden="true" />
        )}
        <input ref={ref} type={type} className={classes} onChange={handleChange} {...autofillOff} {...props} />
        {trailingIcon && (
          <i className={`fa-solid ${trailingIcon} ${styles.trailingIcon}`} aria-hidden="true" />
        )}
      </div>
    );
  }

  return <input ref={ref} type={type} className={classes} onChange={handleChange} {...autofillOff} {...props} />;
});
